/**
 * Description:
 * module with helper functions to create GPT objects, preferably used in combination with html data attributes as below
 * 
 *
 * Sample of how to define a new ad slot:
 *
    <adslot
        data-g-ad-format="panorama,wide_skyscraper"   // separate multiple formats with a comma
        data-g-ad-device="desktop"                    // mobile,tablet,desktop
        data-g-ad-position="top-desktop" />             // match this towards the "pos" in DFP
 *
 */
import almostRandom from '../../../../utils/almost-random';

/**
 * @returns array of arrays of 2 elements with [width, height]:
 * @param {string} adFormat - accepts format value of:  panorama, wide_skyscraper, half_page, medium_rectangle, mobile_rectangle
 */
const mapSingleAdFormatToSizes = adFormat => {
    const viewPortMapping = {
        panorama: [[980, 240], [980, 160], [980, 120], [970, 250]],
        wide_skyscraper: [[160, 600]],
        half_page: [[300, 600]],
        medium_rectangle: [[300, 250]],
        mobile_rectangle: [[320, 320]],
        mobile_wideboard: [[320, 160]]
    };
    return viewPortMapping[adFormat];
};

/**
 * @returns array of arrays of 2 elements with slot [width, height]:
 * @param {string} adFormats - accepts one or more ',' separated format values
 */
const mapManyAdFormatsToSizes = adFormats => {
    return adFormats
        .split(',')
        .map(adFormat => mapSingleAdFormatToSizes(adFormat))
        .reduce((pre, cur) => pre.concat(cur));
};

/**
 * @param {string} device - accepts one of: mobile, tablet, desktop, desktop-lg
 * @returns array with first elements: [min width, max width]
 */
const mapDeviceNameToViewport = device => {
    let deviceViewportMappings = {
        mobile: [0, 449],
        tablet: [450, 1249],
        desktop: [1250, 0],
        'desktop-lg': [1600, 0]
    };
    return deviceViewportMappings[device];
};

/**
 * @param {string} adFormats - accepts one or more ',' separated format values
 * @param {string} device - accepts one of: mobile, tablet, desktop, desktop-lg
 */
const createGptSizeMapping = (adFormats, device) => {
    if (!adFormats || !device) {
        console.warn(
            'Missing some of the required arguments: adFormats, device',
            'background:red; color:white; font-size:14px;'
        );
    }

    let [minWidth, maxWidth] = mapDeviceNameToViewport(device);
    let sizes = mapManyAdFormatsToSizes(adFormats);

    let sizeMap = [
        {
            viewport: [minWidth, 0],
            slot: sizes
        }
    ];

    if (maxWidth > 0) {
        sizeMap.push({
            viewport: [maxWidth + 1, 0], // add 1 pixel to make max-width inclusive
            slot: []
        });
    }

    if (0 < minWidth) {
        sizeMap.push({
            viewport: [0, 0],
            slot: []
        });
    }

    return sizeMap;
};

/**
 * @param {string} adFormats - accepts one or more ',' separated format values
 * @param {string} position
 * @param {string} device - accepts one of: mobile, tablet, desktop, desktop-lg
 */
const createGptSlotObject = (adFormats, position, device) => {
    if (!adFormats || !position || !device) {
        console.warn(
            'Constructor is missing some of the required arguments: adFormats, position, device',
            'background:red; color:white; font-size:14px;'
        );
    }

    let adSizes = mapManyAdFormatsToSizes(adFormats);
    let [minWidth, maxWidth] = mapDeviceNameToViewport(device);
    let htmlDomId = `${position}-${almostRandom.get()}`;
    let collapseIfEmpty = true;
    let lazyLoading = false;
    let viewportSizeMappings = [];

    viewportSizeMappings.push({
        viewportSize: { width: minWidth, height: 0 },
        viewportWidth: minWidth,
        viewportHeight: 0,
        slotSizes: adSizes.map(size => ({
            width: size[0],
            height: size[1]
        }))
    });

    if (maxWidth > 0) {
        viewportSizeMappings.push({
            viewportSize: { width: maxWidth, height: 0 },
            viewportWidth: maxWidth,
            viewportHeight: 0,
            slotSizes: []
        });
    }

    if (0 < minWidth) {
        viewportSizeMappings.push({
            viewportSize: { width: 0, height: 0 },
            viewportWidth: 0,
            viewportHeight: 0,
            slotSizes: []
        });
    }

    return {
        htmlDomId: htmlDomId,
        viewportSizeMappings: viewportSizeMappings,
        position: position,
        collapseIfEmpty: collapseIfEmpty,
        lazyLoading: lazyLoading,
        customTargetingParameters: {
            pos: position
        }
    };
};

export {
    createGptSlotObject,
    createGptSizeMapping,
    mapSingleAdFormatToSizes,
    mapManyAdFormatsToSizes,
    mapDeviceNameToViewport
};



// WEBPACK FOOTER //
// ./src/EbaySweden.TouchWeb/static/script/app/ui/layout/ads/ad-slot-utilities.js